import { useEffect, useState } from 'react';
import type {
  CaptchaProvider,
  CaptchaEndpoint,
  CaptchaConfigUpdateRequest,
} from '@pryma/auth-shared';
import { Button, Field, Input, FormSection } from '../../ui';
import { type CaptchaSettings as CaptchaSettingsValue } from '../../hooks/useCaptchaSettings';
import './CaptchaSettings.css';

const PROVIDER_LABELS: Record<CaptchaProvider, string> = {
  turnstile: 'Cloudflare Turnstile',
  recaptcha_v3: 'Google reCAPTCHA v3',
  hcaptcha: 'hCaptcha',
};

interface EndpointGroup {
  label: string;
  endpoints: { id: CaptchaEndpoint; label: string }[];
}

const ADMIN_ENDPOINTS: EndpointGroup = {
  label: 'Admin portal',
  endpoints: [
    { id: 'admin_login', label: 'Login' },
    { id: 'admin_register', label: 'Register' },
    { id: 'admin_forgot_password', label: 'Forgot password' },
    { id: 'admin_magic_link', label: 'Magic link request' },
    { id: 'admin_otp', label: 'OTP request' },
  ],
};

const END_USER_ENDPOINTS: EndpointGroup = {
  label: 'End-user auth',
  endpoints: [
    { id: 'end_user_login', label: 'Login' },
    { id: 'end_user_register', label: 'Register' },
    { id: 'end_user_magic_link', label: 'Magic link request' },
    { id: 'end_user_otp', label: 'OTP request' },
  ],
};

export interface CaptchaSettingsProps {
  scope: 'platform' | 'project';
  settings: CaptchaSettingsValue | undefined;
  isLoading: boolean;
  isSaving: boolean;
  onSave: (req: CaptchaConfigUpdateRequest) => Promise<unknown>;
  canEdit?: boolean;
}

// Single form for either platform-level or project-level CAPTCHA config.
// The scope just controls which endpoint groups are presented and the
// title — submission shape is identical.
export function CaptchaSettings({
  scope,
  settings,
  isLoading,
  isSaving,
  onSave,
  canEdit = true,
}: CaptchaSettingsProps) {
  const groups = scope === 'platform' ? [ADMIN_ENDPOINTS] : [END_USER_ENDPOINTS];

  const [provider, setProvider] = useState<CaptchaProvider>('turnstile');
  const [siteKey, setSiteKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [scoreThreshold, setScoreThreshold] = useState(0.5);
  const [enabled, setEnabled] = useState(false);
  const [enabledEndpoints, setEnabledEndpoints] = useState<Set<CaptchaEndpoint>>(new Set());
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!settings) return;
    setProvider(settings.provider ?? 'turnstile');
    setSiteKey(settings.siteKey ?? '');
    setSecretKey('');
    setScoreThreshold(settings.scoreThreshold ?? 0.5);
    setEnabled(settings.enabled);
    setEnabledEndpoints(new Set(settings.enabledEndpoints as CaptchaEndpoint[]));
  }, [settings]);

  const showFlash = (kind: 'success' | 'error', text: string) => {
    setFlash({ kind, text });
    window.setTimeout(() => setFlash(null), 4000);
  };

  const toggleEndpoint = (endpoint: CaptchaEndpoint) => {
    setEnabledEndpoints(prev => {
      const next = new Set(prev);
      if (next.has(endpoint)) {
        next.delete(endpoint);
      } else {
        next.add(endpoint);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    const payload: CaptchaConfigUpdateRequest = {
      provider,
      siteKey,
      // Empty secretKey means "keep existing" — the API treats undefined as omit-to-keep.
      secretKey: secretKey ? secretKey : undefined,
      scoreThreshold,
      enabledEndpoints: Array.from(enabledEndpoints),
      enabled,
    };

    try {
      await onSave(payload);
      setSecretKey('');
      showFlash('success', 'CAPTCHA settings saved.');
    } catch (err) {
      showFlash('error', (err as Error).message ?? 'Failed to save settings');
    }
  };

  if (isLoading) {
    return <p>Loading CAPTCHA settings…</p>;
  }

  const secretPlaceholder = settings?.hasSecretKey
    ? 'Leave blank to keep the existing secret'
    : 'Paste your provider secret key';

  return (
    <form className="captcha-settings" onSubmit={handleSubmit}>
      {flash && (
        <div className={`captcha-settings__flash captcha-settings__flash--${flash.kind}`}>
          {flash.text}
        </div>
      )}

      <FormSection title="Provider" description="Pick which CAPTCHA service to use.">
        <Field label="Provider" htmlFor="captcha-provider">
          <select
            id="captcha-provider"
            value={provider}
            onChange={e => setProvider(e.target.value as CaptchaProvider)}
            disabled={!canEdit}
          >
            {(Object.keys(PROVIDER_LABELS) as CaptchaProvider[]).map(p => (
              <option key={p} value={p}>
                {PROVIDER_LABELS[p]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Site key (public)" htmlFor="captcha-site-key">
          <Input
            id="captcha-site-key"
            value={siteKey}
            onChange={e => setSiteKey(e.target.value)}
            placeholder="Your provider site key"
            disabled={!canEdit}
          />
        </Field>

        <Field label="Secret key" htmlFor="captcha-secret-key">
          <Input
            id="captcha-secret-key"
            type="password"
            value={secretKey}
            onChange={e => setSecretKey(e.target.value)}
            placeholder={secretPlaceholder}
            disabled={!canEdit}
            autoComplete="new-password"
          />
        </Field>
      </FormSection>

      <FormSection
        title="Score threshold"
        description="For score-based providers (reCAPTCHA v3, hCaptcha Enterprise) — tokens scoring below this fail verification."
      >
        <div className="captcha-settings__threshold">
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={scoreThreshold}
            onChange={e => setScoreThreshold(parseFloat(e.target.value))}
            disabled={!canEdit}
          />
          <span>{scoreThreshold.toFixed(2)}</span>
        </div>
      </FormSection>

      <FormSection
        title="Protected endpoints"
        description="Which authentication endpoints should require a CAPTCHA token."
      >
        {groups.map(group => (
          <div key={group.label}>
            <p className="captcha-settings__hint">{group.label}</p>
            <div className="captcha-settings__endpoints">
              {group.endpoints.map(endpoint => (
                <label key={endpoint.id}>
                  <input
                    type="checkbox"
                    checked={enabledEndpoints.has(endpoint.id)}
                    onChange={() => toggleEndpoint(endpoint.id)}
                    disabled={!canEdit}
                  />
                  {endpoint.label}
                </label>
              ))}
            </div>
          </div>
        ))}
      </FormSection>

      <FormSection title="Enable CAPTCHA">
        <label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            disabled={!canEdit}
          />{' '}
          Turn CAPTCHA verification on for this {scope === 'platform' ? 'platform' : 'project'}
        </label>
      </FormSection>

      {canEdit && (
        <Button type="submit" loading={isSaving}>
          {isSaving ? 'Saving…' : 'Save settings'}
        </Button>
      )}
    </form>
  );
}
